import React from 'react';
import { MapPin, ArrowRight, Phone, CheckCircle, Gift, Calculator } from 'lucide-react';
import Navigation from './Navigation';
import SEO from './SEO';
import ContactForm from './ContactForm';
import SuburbSnapshot from './SuburbSnapshot';
import { faqSchema, breadcrumbSchema } from '../utils/schemas';
import { COSTCO_WAREHOUSES } from '../data/costcoWarehouses';

const CostcoLocationTemplate = ({
  warehouseName,
  suburb,
  state,
  stateShort,
  address,
  nearbySuburbs,
  description,
  localContext,
  faqs,
  path,
  suburbData,
  surroundingSuburbs,
}) => {
  const nearbyList = nearbySuburbs || [];
  // Internal-linking cluster: the other Costco warehouses, grouped by state with this
  // page's own state first (most relevant). Links point at existing /costco/* URLs only.
  const otherByState = COSTCO_WAREHOUSES
    .filter((w) => w.path !== path)
    .reduce((acc, w) => { (acc[w.state] = acc[w.state] || []).push(w); return acc; }, {});
  const stateOrder = Object.keys(otherByState).sort(
    (a, b) => (a === stateShort ? -1 : b === stateShort ? 1 : a.localeCompare(b))
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Costco ${warehouseName} Member Loans | ${stateShort}`}
        description={`Costco Shop Card offer for Executive Members near ${warehouseName}, ${stateShort}. Home loans, car loans, and more. Serving ${nearbyList.slice(0, 3).join(', ')} and surrounds.`}
        path={path}
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Costco', url: '/costco' },
            { name: warehouseName, url: path },
          ]),
          ...(faqs
            ? [faqSchema(faqs.map((f) => ({ question: f.q, answer: f.a })))]
            : []),
        ]}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#00594C] to-[#003d35]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <img
              src="/images/costco-logo.png"
              alt="Costco Wholesale"
              className="h-14 mx-auto mb-6"
            />
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
              <MapPin className="w-4 h-4 text-[#99DF63]" />
              <span className="text-sm font-medium">{suburb}, {stateShort}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Costco <span className="text-[#99DF63]">{warehouseName}</span>
              <span className="block text-2xl md:text-3xl mt-2 font-normal text-gray-200">Member Loan Partner</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Exclusive Costco Shop Cards for Executive Members on settled loans. Terms and conditions apply.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/costco#calculator"
                className="inline-flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg"
              >
                <Calculator className="w-5 h-5" />
                <span>Calculate Your Shop Card</span>
              </a>
              <a
                href="#book-appointment"
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all border border-white/30"
              >
                <Phone className="w-5 h-5" />
                <span>0468 007 056</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#00594C] mb-6">
              Your Costco Loan Partner Near {warehouseName}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">{description}</p>
            {address && (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-[#00594C] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-[#00594C] mb-1">Costco {warehouseName}</p>
                    <p className="text-gray-600">{address}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Suburb Property Snapshot */}
      <SuburbSnapshot data={suburbData} warehouseName={warehouseName} surroundingSuburbs={surroundingSuburbs} />

      {/* Shop Card Highlight */}
      <section className="py-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Gift className="w-16 h-16 text-[#99DF63] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Earn a Costco Shop Card on Your Loan
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              As a Costco Executive Member, settle a loan through Manage Your Loans and receive a Costco Shop Card. Use it on anything at your {warehouseName} warehouse.
            </p>
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto mb-8">
              <div>
                <p className="text-3xl font-bold text-[#99DF63]">$200</p>
                <p className="text-sm text-gray-300">From $100k loan</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#99DF63]">$1,800</p>
                <p className="text-sm text-gray-300">From $900k loan</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#99DF63]">$4,000</p>
                <p className="text-sm text-gray-300">$2M+ loans</p>
              </div>
            </div>
            <a
              href="/costco#calculator"
              className="inline-flex items-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all"
            >
              <span>Calculate Your Shop Card Value</span>
              <ArrowRight className="w-5 h-5" />
            </a>
            <p className="text-xs text-gray-400 mt-4">
              * Based on fully drawn-down loan amount. Offset and redraw balances excluded. <a href="/costco-terms" className="underline">T&Cs apply</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Nearby Suburbs */}
      {nearbyList.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#00594C] text-center mb-4">
                Serving Suburbs Near Costco {warehouseName}
              </h2>
              <p className="text-gray-600 text-center mb-8">
                We help Costco Executive Members across {suburb} and surrounding suburbs
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {nearbyList.map((s, i) => (
                  <span
                    key={i}
                    className="bg-gray-50 text-[#00594C] px-4 py-2 rounded-full text-sm font-medium border border-gray-200"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Local Context */}
      {localContext && localContext.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#00594C] text-center mb-12">
              Local Lending Insights — {suburb}
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {localContext.map((item, i) => {
                // localContext entries come in two shapes across pages: {title, description}
                // objects OR plain strings. Render both (strings had been silently dropping).
                const isObj = item && typeof item === 'object';
                const heading = isObj ? item.title : null;
                const body = isObj ? item.description : item;
                return (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#99DF63]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#00594C]" />
                    </div>
                    <div>
                      {heading && <h3 className="font-semibold text-[#00594C] mb-1">{heading}</h3>}
                      <p className="text-gray-600 text-sm">{body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#00594C] text-center mb-12">
              Costco Member Loan FAQs
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-gray-50 rounded-xl border border-gray-100 group">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-[#00594C] hover:text-[#99DF63] transition-colors list-none flex items-center justify-between">
                    {faq.q}
                    <svg className="w-5 h-5 transition-transform group-open:rotate-180 flex-shrink-0 ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* General Advice Warning */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white border border-amber-200 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-800">General Advice Warning:</strong> The information provided is general in nature and does not take into account your objectives, financial situation or needs. You should consider whether it is appropriate for you.
            </p>
          </div>
        </div>
      </section>

      {/* Other Costco warehouse locations — internal-linking cluster (existing URLs only) */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#00594C] text-center mb-4">
              Other Costco Warehouse Locations
            </h2>
            <p className="text-gray-600 text-center mb-10">
              We help Costco Executive Members earn a Shop Card on settled loans at warehouses across Australia.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
              {stateOrder.map((st) => (
                <div key={st}>
                  <h3 className="font-bold text-[#00594C] mb-3">
                    {st}{st === stateShort ? ' — near you' : ''}
                  </h3>
                  <ul className="space-y-2">
                    {otherByState[st].map((w) => (
                      <li key={w.path}>
                        <a
                          href={w.path}
                          className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm"
                        >
                          Costco {w.name} ({w.suburb})
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a
                href="/costco"
                className="inline-flex items-center text-[#00594C] font-semibold hover:text-[#99DF63] transition-colors"
              >
                View all Costco member loan locations
                <ArrowRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactForm />

      {/* Costco Footer */}
      <footer className="bg-white border-t-2 border-gray-100">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#00594C]">Costco Loan Services</h3>
                <ul className="space-y-2">
                  <li><a href="/costco#calculator" className="text-gray-600 hover:text-[#99DF63] transition-colors">Shop Card Calculator</a></li>
                  <li><a href="/costco#book-appointment" className="text-gray-600 hover:text-[#99DF63] transition-colors">Book Appointment</a></li>
                  <li><a href="/costco-terms" className="text-gray-600 hover:text-[#99DF63] transition-colors">Terms & Conditions</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#00594C]">Contact</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="tel:+61468007056" className="text-gray-600 hover:text-[#99DF63] transition-colors flex items-center space-x-2">
                      <Phone className="w-4 h-4" />
                      <span>0468 007 056</span>
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#00594C]">Legal</h3>
                <ul className="space-y-2">
                  <li><a href="/privacy-policy" className="text-gray-600 hover:text-[#99DF63] transition-colors">Privacy Policy</a></li>
                  <li><a href="/complaints-policy" className="text-gray-600 hover:text-[#99DF63] transition-colors">Complaints Policy</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-lg mb-4 text-[#00594C]">Locations</h3>
                <ul className="space-y-2">
                  <li><a href="/costco/auburn" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Auburn</a></li>
                  <li><a href="/costco/casula" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Casula</a></li>
                  <li><a href="/costco/marsden-park" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Marsden Park</a></li>
                  <li><a href="/costco/ardeer" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Ardeer</a></li>
                  <li><a href="/costco/epping" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Epping</a></li>
                  <li><a href="/costco/ringwood" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Ringwood</a></li>
                  <li><a href="/costco/north-lakes" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">North Lakes</a></li>
                  <li><a href="/costco/gold-coast" className="text-gray-600 hover:text-[#99DF63] transition-colors text-sm">Gold Coast</a></li>
                </ul>
              </div>
            </div>
            <div className="text-center text-xs text-gray-400 space-y-3 pt-6 border-t border-gray-200">
              <p>
                The information provided on this page is general in nature and does not take into account your objectives, financial situation or needs. You should consider whether it is appropriate for you.
              </p>
              <p>
                Manage Your Loans Pty Ltd ABN 34 652 442 790 is a Credit Representative (540988) of Home Mortgage Plus Pty Ltd ACN 105 991 839 (ACL 392200).
                A referral arrangement is in place between MYL and Costco; however, they are not otherwise affiliated in any other way.
              </p>
              <p>&copy; {new Date().getFullYear()} Manage Your Loans. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CostcoLocationTemplate;
